import type { RfqProject, RfqSubmission } from '../types';
import { buildTimberApprovalUpdate } from './timberRfqPrePortalVersion';
import { uploadLegacyQuoteFile, validateLegacyQuoteFile } from './legacyQuoteFileUtils';
import {
  type TimberQuoteSubmissionEdits,
  type UpdateTimberQuoteSubmissionDeps,
  updateTimberQuoteSubmission,
} from './rfqTimberQuoteEdit';
import { recordSupplierBillForRfqAward } from './supplierBillIngestion';
import { approvedSubmissionIdsOnRfqProject } from './rfqApprovedQuoteCoverage';
import { stampPmQuoteInputAdjustment } from './rfqPmQuoteInputStamp';

export type LegacyQuoteSubmissionEdits = TimberQuoteSubmissionEdits & {
  receivedAt?: Date;
  replacePdfFile?: File;
  markApproved?: boolean;
};

export type LegacyQuoteActor = {
  byUserId: string;
  byUserName: string;
  orgName: string;
};

export async function updateLegacyQuoteSubmission(
  deps: UpdateTimberQuoteSubmissionDeps,
  rfqProject: RfqProject,
  orgId: string,
  sub: RfqSubmission,
  packageId: string,
  releaseId: string,
  edits: LegacyQuoteSubmissionEdits,
  actor?: LegacyQuoteActor,
): Promise<void> {
  if (!sub.id || !rfqProject.id) return;

  if (edits.replacePdfFile) {
    const fileError = validateLegacyQuoteFile(edits.replacePdfFile);
    if (fileError) throw new Error(fileError);
    const { storagePath, downloadUrl } = await uploadLegacyQuoteFile(orgId, packageId, edits.replacePdfFile);
    await deps.updateRfqSubmission(sub.id, {
      storagePath,
      downloadUrl,
      fileName: edits.replacePdfFile.name,
      sizeBytes: edits.replacePdfFile.size,
    });
  }

  const timberEdits: TimberQuoteSubmissionEdits = {};
  if (edits.species?.trim()) timberEdits.species = edits.species.trim();
  if (edits.dryness) timberEdits.dryness = edits.dryness;
  if (edits.quoteTotal != null) timberEdits.quoteTotal = edits.quoteTotal;
  if (edits.leadTimeWeeksMin != null || edits.leadTimeWeeksMax != null) {
    timberEdits.leadTimeWeeksMin = edits.leadTimeWeeksMin;
    timberEdits.leadTimeWeeksMax = edits.leadTimeWeeksMax;
  }
  if (edits.quotedStructures !== undefined) {
    timberEdits.quotedStructures = edits.quotedStructures;
  }

  if (Object.keys(timberEdits).length > 0) {
    await updateTimberQuoteSubmission(deps, rfqProject, orgId, sub, timberEdits);
  }

  if (edits.receivedAt) {
    await deps.updateRfqSubmission(sub.id, { createdAt: edits.receivedAt });
  }

  if (edits.markApproved === true) {
    await deps.updateRfqProject(
      rfqProject.id,
      buildTimberApprovalUpdate(rfqProject, releaseId, sub.id, true),
    );
    if (actor && sub.id) {
      const species = edits.species?.trim() || sub.quotedSpecies || sub.fileName || 'Timber quote';
      await recordSupplierBillForRfqAward({
        project: rfqProject,
        category: 'timber',
        orgId,
        orgName: actor.orgName,
        submissionKey: sub.id,
        amount: edits.quoteTotal ?? sub.quoteTotal,
        description: `Timber quote — ${species}`,
        byUserId: actor.byUserId,
        byUserName: actor.byUserName,
      });
    }
  } else if (edits.markApproved === false) {
    await deps.updateRfqProject(
      rfqProject.id,
      buildTimberApprovalUpdate(rfqProject, releaseId, sub.id, false),
    );
  } else if (
    actor
    && edits.quoteTotal != null
    && sub.id
    && approvedSubmissionIdsOnRfqProject(rfqProject).has(sub.id)
  ) {
    const species = edits.species?.trim() || sub.quotedSpecies || sub.fileName || 'Timber quote';
    await recordSupplierBillForRfqAward({
      project: rfqProject,
      category: 'timber',
      orgId,
      orgName: actor.orgName,
      submissionKey: sub.id,
      amount: edits.quoteTotal,
      description: `Timber quote — ${species}`,
      byUserId: actor.byUserId,
      byUserName: actor.byUserName,
    });
  }

  const touchedQuoteFields =
    Object.keys(timberEdits).length > 0
    || !!edits.replacePdfFile
    || !!edits.receivedAt;
  if (actor && touchedQuoteFields) {
    await deps.updateRfqSubmission(
      sub.id,
      stampPmQuoteInputAdjustment({ uid: actor.byUserId, name: actor.byUserName }),
    );
  }
}
